import type { FormProps } from "antd"
import { Form, message } from "antd"
import Title from "antd/es/typography/Title"
import { t } from "i18next"
import React, { useState } from "react"
import "react-quill/dist/quill.snow.css"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { Paths } from "~/constants"
import { FaqItem } from "~/modules/parent-question/components"
import FeaturedImage from "~/modules/post/pages/FeaturedImage"
import { useUiBuilder } from "~/modules/ui-builder/services/ui-builder"
import { FieldType, MediaData } from "~/types/types"
import { RootState } from "../../auth/services/redux/store"
import pageService from "../services/page"
import PageForm from "./PageForm"
import Publish from "./Publish"

const AddNewPage = () => {
  const [quillValue, setQuillValue] = React.useState<string>("")
  const [form] = Form.useForm()
  const [status, setStatus] = useState("Draft")
  const [isUIBuilderPage, setIsUIBuilderPage] = useState(false)
  const navigate = useNavigate()
  const { addPage, checkSlug } = pageService()
  const [selectedMetaImage, setSelectedMetaImage] = useState<MediaData | null>(null)
  const [publishDate, setPublishDate] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<MediaData | null>(null)
  const account = useSelector((state: RootState) => state.account)
  const [language, setLanguages] = useState("vi")
  const pageServices = pageService()
  const [selectedFaqs, setSelectedFaqs] = useState<FaqItem[]>([])
  const [faqEnabled, setFaqEnabled] = useState(false)

  const { createNewBlockContent } = useUiBuilder()

  const onFinish: FormProps<FieldType>["onFinish"] = async values => {
    const dataPayload: any = {
      name: values.name,
      slug: values.slug,
      content: values.content || "",
      excerpt: values.excerpt,
      publishedAt: status === "Draft" ? null : new Date().toISOString(),
      image: selectedImage,
      isUseUIBuilder: isUIBuilderPage,
      locale: language,
      author: account.user,
      SEO: {
        id: 0,
        metaTitle: values.metaTitle ? values.metaTitle : values.name,
        metaDescription: values.metaDescription ? values.metaDescription : values.name,
      },
      faq_enabled: faqEnabled,
      faqs: faqEnabled ? selectedFaqs.map((faq, index) => ({
        __component: "content.faq",
        question: faq.question,
        answer: faq.answer,
        order: index,
      })) : [],
    }

    try {
      const createdPage = await pageServices.addPage({ data: dataPayload })
      if (isUIBuilderPage) {
        const createdBlockContent = await createNewBlockContent({ page: createdPage.data.id })
        navigate(`/ui-builder/${createdBlockContent.data.id}`)
        return
      }

      message.success(t("Page added successfully!"))

      form.resetFields()

      navigate(Paths.PAGES.LIST.PATH)
    } catch (error) {
      message.error(t("Failed to add post. Please try again."))
    }
  }

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = () => { }

  const onChangeMetaImage = (media: MediaData | null) => {
    setSelectedMetaImage(media)
  }

  const handleSelectMedia = (media: MediaData | null) => {
    setSelectedImage(media)
  }

  const onChangeFaqs = (faqs: FaqItem[]) => {
    setSelectedFaqs(faqs)
  }

  const onFaqEnabledChange = (enabled: boolean) => {
    setFaqEnabled(enabled)
  }

  return (
    <div className="p-4">
      <Title level={2} className="m-0">
        {t("Add New Page")}
      </Title>
      <div className="flex justify-between">
        <PageForm
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          quillValue={quillValue}
          setQuillValue={setQuillValue}
          onChangeMetaImage={onChangeMetaImage}
          isUIBuilderPage={isUIBuilderPage}
          selectedFaqs={selectedFaqs}
          onChangeFaqs={onChangeFaqs}
          faqEnabled={faqEnabled}
          onFaqEnabledChange={onFaqEnabledChange}
        />
        <div className=" w-[22%] pt-2 ">
          <Publish
            onPageTypeChanged={isUseUIBuilder => {
              setIsUIBuilderPage(isUseUIBuilder)
            }}
            onSubmit={form.submit}
            isCreateEdit={true}
            setStatus={setStatus}
            status={status}
            setPublishDate={setPublishDate}
            language={language}
            setLanguages={setLanguages}
          />
          <FeaturedImage selectedMedia={selectedImage} setSelectedMedia={handleSelectMedia} />
        </div>
      </div>
    </div>
  )
}

export default AddNewPage
